import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FacebookShareButton } from 'react-share';

import AccountCard from '../../../../../services/cards/components/AccountCard';
import Button from '../../../Button';
import useCollageDesignerSuccess from './hooks/useCollageDesignerSuccess';
import Spinner from '../../../Spinner';
import useAccount from '../../../hooks/useAccount';
import useOpenAlert from '../../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../../util/normalize-errors';
import CollageDesignerEmailModal from './components/CollageDesignerEmailModal/CollageDesignerEmailModal';

const propTypes = {
  accountCard: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  onGoBack: PropTypes.func,
  onAddToCart: PropTypes.func,
  addToCartLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

const CollageDesignerSuccess = ({
  accountCard,
  onGoBack,
  onAddToCart,
  addToCartLabel = <Fragment>Order in 8x10 print</Fragment>,
}) => {
  const openAlert = useOpenAlert();
  const { account } = useAccount();
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewDownloadUrl, setPreviewDownloadUrl] = useState('');
  const [preparingDownload, setPreparingDownload] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloadSent, setDownloadSent] = useState(false);

  const {
    previewUrl: latestPreviewUrl,
    previewDownloadUrl: latestPreviewDownloadUrl,
    stopPolling,
    onAccountCardsPhotoDownloadCreate,
  } = useCollageDesignerSuccess({
    accountCardId: accountCard?.id,
    prepareDownload: preparingDownload,
  });

  useEffect(() => {
    if (latestPreviewUrl) {
      setPreviewUrl(latestPreviewUrl);
      stopPolling();
    }
  }, [latestPreviewUrl]);

  const onCollageDownload = (url = previewDownloadUrl) => {
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.target = '_blank';
    downloadLink.download = 'My Collage.jpg';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handleDownload = async (emailAddress = account?.email_addressz) => {
    if (submitting) {
      return;
    }

    if (!emailAddress) {
      setEmailModalOpen(true);
      return;
    }

    try {
      setSubmitting(true);
      await onAccountCardsPhotoDownloadCreate(emailAddress);
      setDownloadSent(true);
      openAlert({
        message: 'Your hi-res download is processing. Check your email soon!',
        type: 'success',
      });
    } catch (e) {
      openAlert({
        message: 'There was an error generating your hi-res download.',
        info: getErrorString(e),
      });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (latestPreviewDownloadUrl && !previewDownloadUrl) {
      onCollageDownload(latestPreviewDownloadUrl);
      setPreviewDownloadUrl(latestPreviewDownloadUrl);
      setPreparingDownload(false);
    }
  }, [latestPreviewDownloadUrl]);

  if (!previewUrl) {
    return (
      <div className="w-100 h-100 d-flex align-items-center justify-content-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="collage-designer-success">
      <div>
        <AccountCard
          viewOnly
          className="account-card-cart-item-preview"
          onElementConfigurationChange={() => {}}
          accountCard={accountCard}
          animatable={false}
          disablePhotoWatermarkOverlay
        />
        {onGoBack && (
          <div className="text-center">
            <Button
              pill
              btnOutline
              onClick={onGoBack}
              disabled={preparingDownload || submitting}
              className="mt-2 mb-4 mb-md-0 px-4"
            >
              Continue editing
            </Button>
          </div>
        )}
      </div>
      <div>
        {onAddToCart && (
          <Button
            btnBlock
            disabled={preparingDownload || submitting}
            onClick={onAddToCart}
          >
            {addToCartLabel}
          </Button>
        )}
        <Button
          btnBlock
          disabled={preparingDownload || submitting || downloadSent}
          onClick={() => handleDownload()}
        >
          {(() => {
            if (downloadSent) {
              return 'Check your email soon!';
            }
            if (submitting || preparingDownload) {
              return 'Processing...';
            }

            return 'Download in hi-res';
          })()}
        </Button>
        <FacebookShareButton
          url={previewUrl}
          disabled={preparingDownload || submitting}
          className="btn btn-outline-primary btn-block"
        >
          Share
        </FacebookShareButton>
      </div>
      <CollageDesignerEmailModal
        forceOpen={emailModalOpen}
        onSubmit={emailAddress => handleDownload(emailAddress)}
        onClose={() => setEmailModalOpen(false)}
      />
    </div>
  );
};

CollageDesignerSuccess.propTypes = propTypes;

export default CollageDesignerSuccess;
